import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Check if user has active database package
 */
export const checkDatabaseAccess = async (userId) => {
  try {
    const accessDoc = await getDoc(doc(db, 'databaseAccess', userId));
    
    if (!accessDoc.exists()) {
      return { 
        success: true, 
        hasAccess: false, 
        remainingContacts: 0 
      };
    }

    const data = accessDoc.data();
    const now = new Date();
    const expiryDate = data.expiryDate?.toDate();

    // Check if package is expired
    if (expiryDate && expiryDate < now) {
      return { 
        success: true, 
        hasAccess: false, 
        remainingContacts: 0,
        expired: true 
      };
    }

    return {
      success: true,
      hasAccess: true,
      remainingContacts: data.remainingContacts || 0,
      totalContacts: data.totalContacts || 0,
      packageName: data.packageName,
      expiryDate: expiryDate
    };
  } catch (error) {
    console.error('Error checking database access:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if a specific contact is already unlocked
 */
export const isContactUnlocked = async (employerId, seekerId) => {
  try {
    const unlockedDoc = await getDoc(
      doc(db, 'unlockedContacts', `${employerId}_${seekerId}`)
    );
    return unlockedDoc.exists();
  } catch (error) {
    console.error('Error checking unlocked contact:', error);
    return false;
  }
};

/**
 * Unlock a contact (deduct from quota and save)
 */
export const unlockContact = async (employerId, seekerData) => {
  try {
    // Check if already unlocked
    const alreadyUnlocked = await isContactUnlocked(employerId, seekerData.id);
    if (alreadyUnlocked) {
      return { 
        success: false, 
        error: 'Contact already unlocked',
        alreadyUnlocked: true 
      };
    }

    // Check access and quota
    const accessCheck = await checkDatabaseAccess(employerId);
    if (!accessCheck.success || !accessCheck.hasAccess) {
      return { 
        success: false, 
        error: 'No active database package' 
      };
    }

    if (accessCheck.remainingContacts <= 0) {
      return { 
        success: false, 
        error: 'No remaining contacts in your quota' 
      };
    }

    // Save unlocked contact
    await setDoc(doc(db, 'unlockedContacts', `${employerId}_${seekerData.id}`), {
      employerId,
      seekerId: seekerData.id,
      seekerName: `${seekerData.firstName} ${seekerData.lastName}`,
      seekerEmail: seekerData.email,
      seekerPhone: seekerData.phone,
      unlockedAt: serverTimestamp(),
      seekerProfile: seekerData.seekerProfile || null
    });

    // Deduct from quota
    await updateDoc(doc(db, 'databaseAccess', employerId), {
      remainingContacts: increment(-1),
      lastUsedAt: serverTimestamp()
    });

    return { 
      success: true, 
      remainingContacts: accessCheck.remainingContacts - 1 
    };
  } catch (error) {
    console.error('Error unlocking contact:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all unlocked contacts for an employer
 */
export const getUnlockedContacts = async (employerId) => {
  try {
    const q = query(
      collection(db, 'unlockedContacts'),
      where('employerId', '==', employerId)
    );
    const snapshot = await getDocs(q);
    
    const contacts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, contacts };
  } catch (error) {
    console.error('Error getting unlocked contacts:', error);
    return { success: false, error: error.message, contacts: [] };
  }
};

/**
 * Grant database access (after package purchase)
 */
export const grantDatabaseAccess = async (userId, packageData) => {
  try {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + (packageData.durationDays || 30));

    await setDoc(doc(db, 'databaseAccess', userId), {
      userId,
      packageId: packageData.id,
      packageName: packageData.name,
      totalContacts: packageData.maxContacts,
      remainingContacts: packageData.maxContacts,
      purchasedAt: serverTimestamp(),
      expiryDate: expiryDate,
      active: true
    });

    // Also persist paymentId on user profile document to satisfy payment status checks
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        paymentId: packageData.paymentId || true
      });
      console.log('✅ Persisted user paymentId on granting database access');
    } catch (userErr) {
      console.warn('⚠️ Could not update user paymentId:', userErr);
    }

    return { success: true };
  } catch (error) {
    console.error('Error granting database access:', error);
    return { success: false, error: error.message };
  }
};
